import "shared/extensions/string";

import { Navigate, useNavigate, useParams } from "react-router-dom";

import { AuthError } from "./AuthError";
import { AuthSsoState } from "core/state/authSsoState";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { DestinationState } from "shared/core/state/destinationState";
import { UseAuthResult } from "core/auth/model";
import ViewLoading from "shared/UI/Spinners/ViewLoading";
import { capitalizeString } from "core/helpers/utils";
import { useCurrentUserFunctionRequest, useUpdateUserTimezoneFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useUserContext } from "core/state/UserContext/UserContext";
import { appInsightsLogger } from "core/monitoring/AppInsights";

interface AuthProps {
  sso?: boolean;
  auth: UseAuthResult;
}

export const Auth = ({ auth, sso }: AuthProps) => {
  const { provider } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, saveUser, isAdditionalInfoComplete } = useUserContext();
  const clientCode = ClientInfoState.getCode();
  const { trigger: updateUserTimezone } = useUpdateUserTimezoneFunctionRequest({ xClientCode: clientCode });
  const { data: userResponse, isLoading: isCurrentUserLoading, error, mutate } = useCurrentUserFunctionRequest({}, { shouldFetch: !auth.isLoading });
  const hasTimezoneUpdated = useRef(false);

  const handleUserTimezoneUpdate = useCallback(() => {
    if (hasTimezoneUpdated.current) return;
    hasTimezoneUpdated.current = true;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    updateUserTimezone({
      body: {
        TzId: tz
      }
    });
  }, [updateUserTimezone])

  useEffect(() => {
    if (userResponse) {
      saveUser(userResponse);
      handleUserTimezoneUpdate();
    }
  }, [userResponse, saveUser, handleUserTimezoneUpdate]);

  useEffect(() => {
    if (sso) {
      const ssoProvider = provider ? capitalizeString(provider) : AuthSsoState.get();
      const returnPath = provider ? "sso/setup" : "single-sign-on";
      const { REACT_APP_AUTH_AUTHORITY: authServerApi, REACT_APP_AUTH_CLIENT_ID: clientId } = process.env;
      const returnUri = encodeURIComponent(`${window.location.origin}/${returnPath}?provider=${ssoProvider}&status=success&client_id=${clientId}`);
      const uri = `${authServerApi}/sso/${ssoProvider}/setup?clientId=${clientId}&returnUrl=${returnUri}`;
      window.location.assign(uri);
    }
  }, [provider, sso]);

  if (sso) {
    return <ViewLoading label={t("auth.loading")} />;
  }

  if (clientCode === undefined) {
    appInsightsLogger.error("Client Info Error!");
    return <Navigate to="/login" />;
  }

  if (error !== undefined) {
    appInsightsLogger.error("Get Current User Error!", error);

    if (error.status === 401 || error.status === 500) {
      return <AuthError auth={auth} status={error.status} onRetry={mutate} />;
    } else {
      return <ViewLoading label={t("auth.loading")} />;
    }
  }

  const isAuthOrApiCallLoading = auth.isLoading || isCurrentUserLoading;
  if (isAuthOrApiCallLoading || !user) {
    return <ViewLoading label={t("auth.loading")} />;
  }

  if (!isAdditionalInfoComplete()) {
    return <Navigate to="/additional-info" />;
  }

  navigate('/home', { replace: true });
  navigate(DestinationState.get());
  return null;
};
